import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Calendar, 
  BookOpen, 
  Clock, 
  CheckCircle, 
  XCircle, 
  AlertCircle,
  Upload,
  FileText,
  User,
  ArrowRight,
  Settings,
  LogOut
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { HomeworkAssignment, HomeworkSubmission, AttendanceRecord } from '../types';
import { homeworkService, homeworkSubmissionService } from '../services/apiService';
import toast from 'react-hot-toast';

const StudentDashboard: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [attendance, setAttendance] = useState<AttendanceRecord[]>([]);
  const [homeworkAssignments, setHomeworkAssignments] = useState<HomeworkAssignment[]>([]);
  const [submissions, setSubmissions] = useState<HomeworkSubmission[]>([]);
  const [loading, setLoading] = useState(true);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  useEffect(() => {
    if (user?.role === 'student') {
      loadStudentData();
    }
  }, [user]);

  const loadStudentData = async () => {
    setLoading(true);
    try {
      // Load homework assignments
      const homeworkData = await homeworkService.getCurrentStudentHomework();
      
      // Handle both response formats: student format {assignments, submissions} or teacher format [assignments]
      if (Array.isArray(homeworkData)) {
        // Teacher format - just assignments array
        setHomeworkAssignments(homeworkData);
      } else if (homeworkData && homeworkData.assignments) {
        // Student format - object with assignments and submissions
        setHomeworkAssignments(homeworkData.assignments);
      } else {
        // Fallback
        setHomeworkAssignments([]);
      }
      
      // Load submissions separately
      try {
        const submissionsData = await homeworkSubmissionService.getCurrentStudentSubmissions();
        setSubmissions(submissionsData);
      } catch (error) {
        console.warn('Could not load submissions:', error);
        setSubmissions([]);
      }
      
      // TODO: Load attendance records when API is available
      // const attendanceData = await attendanceService.getForStudent(user?.id);
      // setAttendance(attendanceData);
      
      setLoading(false);
    } catch (error) {
      console.error('Error loading student data:', error);
      toast.error('Failed to load data');
      setLoading(false);
    }
  };

  const getAttendanceStatusIcon = (status: string) => {
    switch (status) {
      case 'present':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'absent':
        return <XCircle className="w-5 h-5 text-red-500" />;
      case 'late':
        return <AlertCircle className="w-5 h-5 text-yellow-500" />;
      case 'excused':
        return <Clock className="w-5 h-5 text-blue-500" />;
      default:
        return <AlertCircle className="w-5 h-5 text-gray-500" />;
    }
  };

  const getAttendanceStatusText = (status: string) => {
    switch (status) {
      case 'present':
        return 'Present';
      case 'absent':
        return 'Absent';
      case 'late':
        return 'Late';
      case 'excused':
        return 'Excused';
      default:
        return 'Unknown';
    }
  };

  const getHomeworkStatus = (assignment: HomeworkAssignment) => {
    const submission = submissions.find(s => s.assignmentId === assignment.id);
    const isOverdue = new Date(assignment.dueDate) < new Date();
    
    if (submission) {
      return {
        status: submission.status,
        text: submission.status === 'graded' ? 'Graded' : 'Submitted',
        color: submission.status === 'graded' ? 'text-green-600' : 'text-blue-600',
        bgColor: submission.status === 'graded' ? 'bg-green-100' : 'bg-blue-100'
      };
    } else if (isOverdue) {
      return {
        status: 'overdue',
        text: 'Overdue',
        color: 'text-red-600',
        bgColor: 'bg-red-100'
      };
    } else {
      return {
        status: 'pending',
        text: 'Not Submitted',
        color: 'text-yellow-600',
        bgColor: 'bg-yellow-100'
      };
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="h-10 w-10 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                  <User className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                </div>
              </div>
              <div className="ml-4">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                  Welcome, {user?.name}
                </h1>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Student Dashboard
                </p>
              </div>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={() => navigate('/student-profile')}
                className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
              >
                <Settings className="w-4 h-4 mr-2" />
                Profile
              </button>
              <button
                onClick={handleLogout}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Attendance Section */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center">
                <Calendar className="h-5 w-5 text-blue-600 dark:text-blue-400 mr-2" />
                <h2 className="text-lg font-medium text-gray-900 dark:text-white">
                  Recent Attendance
                </h2>
              </div>
            </div>
            <div className="p-6">
              {attendance.length === 0 ? (
                <div className="text-center py-8">
                  <Calendar className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
                    No attendance records
                  </h3>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    Your attendance will appear here once classes begin.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {attendance.slice(0, 5).map((record) => (
                    <div key={record.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                      <div className="flex items-center">
                        {getAttendanceStatusIcon(record.status)}
                        <div className="ml-3">
                          <p className="text-sm font-medium text-gray-900 dark:text-white">
                            {new Date(record.date).toLocaleDateString()}
                          </p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            {getAttendanceStatusText(record.status)}
                          </p>
                        </div>
                      </div>
                      {record.notes && (
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {record.notes}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Homework Section */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <BookOpen className="h-5 w-5 text-green-600 dark:text-green-400 mr-2" />
                  <h2 className="text-lg font-medium text-gray-900 dark:text-white">
                    Homework Assignments
                  </h2>
                </div>
                <Link
                  to="/student-homework"
                  className="text-sm text-blue-600 hover:text-blue-500 dark:text-blue-400 flex items-center"
                >
                  View All
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Link>
              </div>
            </div>
            <div className="p-6">
              {homeworkAssignments.length === 0 ? (
                <div className="text-center py-8">
                  <BookOpen className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
                    No homework assignments
                  </h3>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    Your homework assignments will appear here.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {homeworkAssignments.slice(0, 5).map((assignment) => {
                    const homeworkStatus = getHomeworkStatus(assignment);
                    return (
                      <div key={assignment.id} className="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h3 className="text-sm font-medium text-gray-900 dark:text-white">
                              {assignment.title}
                            </h3>
                            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                              Due: {new Date(assignment.dueDate).toLocaleDateString()}
                            </p>
                            {assignment.description && (
                              <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                                {assignment.description}
                              </p>
                            )}
                          </div>
                          <div className="ml-4">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${homeworkStatus.color} ${homeworkStatus.bgColor}`}>
                              {homeworkStatus.text}
                            </span>
                          </div>
                        </div>
                        {homeworkStatus.status === 'pending' && (
                          <div className="mt-4">
                            <Link
                              to="/student-homework"
                              className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                            >
                              <Upload className="w-4 h-4 mr-2" />
                              Submit
                            </Link>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CheckCircle className="h-8 w-8 text-green-600 dark:text-green-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                    Attendance Rate
                  </dt>
                  <dd className="text-lg font-medium text-gray-900 dark:text-white">
                    95%
                  </dd>
                </dl>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <FileText className="h-8 w-8 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                    Assignments Due
                  </dt>
                  <dd className="text-lg font-medium text-gray-900 dark:text-white">
                    3
                  </dd>
                </dl>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <BookOpen className="h-8 w-8 text-purple-600 dark:text-purple-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                    Completed
                  </dt>
                  <dd className="text-lg font-medium text-gray-900 dark:text-white">
                    12
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentDashboard;
